<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Journal entries</h1>
            <p class="text-muted mb-0">
              Manual adjustments posted to your accounts
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add journal entry
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <CInput
          v-if="items.length"
          v-model="search"
          placeholder="Search journal no. or description"
          aria-label="Search journal entries"
          class="mb-3"
        >
          <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
        </CInput>

        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
          <div class="text-muted mt-2">Loading journal entries…</div>
        </div>

        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6 font-weight-bold">Couldn't load journal entries</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>

        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-book" size="xl" class="text-muted mb-2" />
          <h2 class="h6 font-weight-bold">No journal entries yet</h2>
          <p class="text-muted">
            Use a journal entry to record adjustments that don't come from an invoice,
            bill or bank transaction.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add journal entry
          </CButton>
        </div>

        <div v-else-if="!filteredItems.length" class="text-center p-4">
          <h2 class="h6 font-weight-bold">No journal entries match your search</h2>
          <CButton color="light" @click="search = ''">
            <CIcon name="cil-x" /> Clear search
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="filteredItems"
              :fields="fields"
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #journalNo="{ item }">
                <td class="text-monospace">{{ item.journalNo }}</td>
              </template>
              <template #totalDebit="{ item }">
                <td class="text-right">RM {{ formatMyr(item.totalDebit) }}</td>
              </template>
              <template #totalCredit="{ item }">
                <td class="text-right">RM {{ formatMyr(item.totalCredit) }}</td>
              </template>
              <template #show_details="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="item._toggled ? `Hide lines of ${item.journalNo}` : `Show lines of ${item.journalNo}`"
                    :aria-expanded="Boolean(item._toggled) ? 'true' : 'false'"
                    @click="toggleDetails(item)"
                  >
                    <CIcon :name="item._toggled ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit ${item.journalNo}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete ${item.journalNo}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
              <template #details="{ item }">
                <CCollapse :show="Boolean(item._toggled)">
                  <CCardBody>
                    <CDataTable
                      :items="item.items"
                      :fields="journalFields"
                      small
                      bordered
                    >
                      <template #show_index="{ index }">
                        <td class="py-2">
                          {{ index + 1 }}
                        </td>
                      </template>
                      <template #show_chartaccount="{ item: line }">
                        <td class="py-2">
                          {{ line.chartAccount ? line.chartAccount.itemDisplay : "" }}
                        </td>
                      </template>
                      <template #debit="{ item: line }">
                        <td class="text-right">RM {{ formatMyr(line.debit) }}</td>
                      </template>
                      <template #credit="{ item: line }">
                        <td class="text-right">RM {{ formatMyr(line.credit) }}</td>
                      </template>
                    </CDataTable>
                  </CCardBody>
                </CCollapse>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="item in filteredItems"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="font-weight-bold text-monospace">{{ item.journalNo }}</div>
                  <div class="small text-muted">{{ item.dateDisplay }}</div>
                  <div v-if="item.description" class="small">{{ item.description }}</div>
                </div>
                <div class="text-right font-weight-bold">
                  RM {{ formatMyr(item.totalDebit) }}
                </div>
              </div>
              <div class="d-flex justify-content-end mt-2">
                <CButton
                  color="light"
                  class="mr-1"
                  :aria-label="`Edit ${item.journalNo}`"
                  @click="onEdit(item)"
                >
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton
                  color="light"
                  :aria-label="`Delete ${item.journalNo}`"
                  @click="showDeleteConfirmation(item)"
                >
                  <CIcon name="cil-trash" class="text-danger" />
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal :show.sync="warningModal" :close-on-backdrop="!deleting" centered>
      <template #header>
        <h5 class="modal-title">Delete journal entry {{ itemToDelete.journalNo }}?</h5>
      </template>
      <p class="mb-0">
        The entry dated {{ itemToDelete.dateDisplay }} for RM
        {{ formatMyr(itemToDelete.totalDebit) }} will be removed from your
        accounts. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />Delete journal entry
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import JournalEntryApi from "@/lib/journalEntryApi";
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";

export default {
  name: "JournalEntryList",
  data() {
    return {
      journalFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
        },
        { key: "description", label: "Description" },
        { key: "show_chartaccount", label: "Account" },
        { key: "debit", label: "Debit", _style: "width:140px" },
        { key: "credit", label: "Credit", _style: "width:140px" },
      ],
      loading: true,
      error: false,
      deleting: false,
      search: "",
      items: [],
      fields: [
        { key: "journalNo", label: "Journal no." },
        { key: "dateDisplay", label: "Date" },
        { key: "description", label: "Description" },
        { key: "totalDebit", label: "Debit", _classes: "text-right" },
        { key: "totalCredit", label: "Credit", _classes: "text-right" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
        },
      ],
      api: new JournalEntryApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          dateDisplay: formatDate(item.date),
        };
      });
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.computedItems;
      return this.computedItems.filter((i) =>
        [i.journalNo, i.description]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "Journal entries"]);
    this.refreshTable();
  },
  methods: {
    formatMyr,
    toggleDetails(item) {
      const original = this.items.find((i) => i.id === item.id);
      if (original) this.$set(original, "_toggled", !original._toggled);
    },
    refreshTable() {
      this.loading = true;
      this.error = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.error = true;
          if (this.items.length) {
            this.$toast.error(
              "Couldn't refresh journal entries. Check your connection and try again."
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/JournalEntry/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    doDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Journal entry deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete the journal entry. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/JournalEntry" });
    },
  },
};
</script>
